/*
 * Purpose: LinkedIn connector panel — client credentials + OAuth in compose
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  getLinkedInClientId,
  hasLinkedInOAuthConfig,
  isLinkedInConnected,
  startLinkedInOAuth,
  disconnectLinkedInOAuth,
} from '@/services/connectors/linkedinOAuth';
import { useSecretStore } from '@/stores/secretStore';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { Briefcase, KeyRound, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';

interface LinkedInConnectorPanelProps {
  onConnected?: () => void;
}

export function LinkedInConnectorPanel({ onConnected }: LinkedInConnectorPanelProps) {
  const setSecret = useSecretStore((s) => s.setSecret);
  const secrets = useSecretStore((s) => s.secrets);
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [clientSecretDraft, setClientSecretDraft] = useState('');
  const connected = isLinkedInConnected();
  const configured = hasLinkedInOAuthConfig();
  const savedClientId = getLinkedInClientId();
  const hasSavedId = secrets.some((s) => s.name === 'LINKEDIN_CLIENT_ID');

  const saveCredentials = async () => {
    if (!useSecretStore.getState().isUnlocked) {
      toast.error('Unlock the vault to save LinkedIn credentials', {
        action: { label: 'Open Setup', onClick: () => { window.location.href = REMEDIATION.setup; } },
      });
      return;
    }
    try {
      if (clientIdDraft.trim()) await setSecret('LINKEDIN_CLIENT_ID', clientIdDraft.trim());
      if (clientSecretDraft.trim()) await setSecret('LINKEDIN_CLIENT_SECRET', clientSecretDraft.trim());
      setClientIdDraft('');
      setClientSecretDraft('');
      toast.success('LinkedIn credentials saved to global vault');
      onConnected?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const onConnect = () => {
    try {
      startLinkedInOAuth('/compose');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Cannot start LinkedIn OAuth');
    }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Briefcase className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">LinkedIn connector</CardTitle>
          <Badge variant={connected ? 'default' : 'secondary'}>
            {connected ? 'Connected' : configured ? 'Ready to connect' : 'Needs app credentials'}
          </Badge>
        </div>
        <CardDescription className="text-xs">
          Register your app at{' '}
          <a
            href="https://www.linkedin.com/developers/apps"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            LinkedIn Developers
          </a>
          , add redirect URL{' '}
          <code className="text-[10px]">http://localhost:3000/oauth/linkedin/callback</code>, request{' '}
          <strong>Sign In with LinkedIn (OIDC)</strong>, then save Client ID + Secret below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <VaultUnlockInline compact />
        {savedClientId && (
          <p className="text-xs text-muted-foreground">
            Client ID on file: <code className="font-mono">{maskId(savedClientId)}</code>
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <Label className="text-xs">LINKEDIN_CLIENT_ID</Label>
            <Input
              className="mt-1 text-xs font-mono"
              placeholder={hasSavedId ? '•••••• (saved)' : 'From LinkedIn app Auth tab'}
              value={clientIdDraft}
              onChange={(e) => setClientIdDraft(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">LINKEDIN_CLIENT_SECRET</Label>
            <Input
              type="password"
              className="mt-1 text-xs font-mono"
              placeholder="From LinkedIn app Auth tab"
              value={clientSecretDraft}
              onChange={(e) => setClientSecretDraft(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={saveCredentials}>
            <KeyRound className="h-3.5 w-3.5 mr-1" />
            Save credentials
          </Button>
          <Button size="sm" onClick={onConnect} disabled={!configured}>
            <Link2 className="h-3.5 w-3.5 mr-1" />
            {connected ? 'Reconnect LinkedIn' : 'Connect LinkedIn'}
          </Button>
          {connected && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                disconnectLinkedInOAuth();
                toast.success('LinkedIn disconnected');
                onConnected?.();
              }}
            >
              <Unlink className="h-3.5 w-3.5 mr-1" />
              Disconnect
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Token stored as <code className="text-[10px]">LINKEDIN_TOKEN</code> for workflow HTTP steps.
          Job apply uses assisted flows unless you have Talent partner APIs.
        </p>
      </CardContent>
    </Card>
  );
}

function maskId(id: string): string {
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}
